import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { categoryAPI } from '../../api'
import { optionKeys } from './useOptions'
import { productKeys } from './useProducts'

// Query key factory for categories
export const categoryKeys = {
  all: ['categories'] as const,
  admin: (params?: { page?: number; perPage?: number; search?: string }) =>
    [...categoryKeys.all, 'admin', params] as const,
  public: () => [...categoryKeys.all, 'public'] as const,
}

// Fetch categories (admin) with pagination and search
export function useCategories(params?: { page?: number; perPage?: number; search?: string }) {
  return useQuery({
    queryKey: categoryKeys.admin(params),
    queryFn: () => categoryAPI.getCategories(params),
  })
}

// Fetch public categories (no auth, for fallback chain)
export function usePublicCategories() {
  return useQuery({
    queryKey: categoryKeys.public(),
    queryFn: () => categoryAPI.getPublicCategories(),
    staleTime: 5 * 60 * 1000, // 5 minutes, categories don't change often
  })
}

// Upsert category mutation
export function useUpsertCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { name: string; image_url?: string | null; description?: string }) =>
      categoryAPI.upsertCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.admin() })
      queryClient.invalidateQueries({ queryKey: categoryKeys.public() })
      queryClient.invalidateQueries({ queryKey: optionKeys.all })
    },
  })
}

// Update a specific category by ID
export function useUpdateCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ categoryId, data }: { categoryId: number; data: Partial<{ name: string; image_url: string | null; description: string }> }) =>
      categoryAPI.updateCategory(categoryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.admin() })
      queryClient.invalidateQueries({ queryKey: categoryKeys.public() })
      queryClient.invalidateQueries({ queryKey: optionKeys.all })
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

// Delete category mutation
export function useDeleteCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (categoryId: number) => categoryAPI.deleteCategory(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.admin() })
      queryClient.invalidateQueries({ queryKey: categoryKeys.public() })
      queryClient.invalidateQueries({ queryKey: optionKeys.all })
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}
